<template>
    <div class="-m-5 md:m-0 h-full flex-1 overflow-hidden">
        <tabulator
            :columns="[
                {
                    title: 'Name', 
                    field: 'name', 
                    sorter: 'string',
                    minWidth: 160,
                },
                {
                    title: 'Username', 
                    field: 'username', 
                    sorter: 'string',
                    minWidth: 150,  
                },
                {
                    title: 'Email', 
                    field: 'email', 
                    sorter: 'string',
                    minWidth: 200,
                },
                {
                    title: 'Roles', 
                    field: 'roles_for_human', 
                    minWidth: 200,
                },
                {
                    title: 'Permissions',
                    field: 'permissions_for_human',
                    minWidth: 300,
                },
                {
                    title: 'Timezone',
                    field: 'timezone_for_human',
                    minWidth: 200,
                    formatter({_cell}) {
                        return _cell.value || '--';
                    }
                },
                {
                    title: 'Status', 
                    field: 'soft_deleted', 
                    minWidth: 130,
                    formatter(cell, params) {
                        if (cell._cell.value) {
                            return `<span class='badge badge-red'>Inactive</span>`;
                        }

                        return `<span class='badge badge-green'>Active</span>`;
                    }
                },
            ]"
            :actions="actions"
            :refresh.sync="refreshTable"
            ajax-path="admin/users"
            @ajaxResponse="(response) => {
                $store.commit('admin/users/setItems', response);
            }"
        />        
        <app-modal
            :show.sync="showEditModal"
            width="md:w-1/2 lg:w-1/4"
            @close="editModalClosed"
        >
            <template slot="header">Edit User</template>
            <template slot="body">                
                <app-input
                    v-model="editForm.name"
                    placeholder="Name"
                    :error="editForm.errors.get('name')"
                    @updated="editForm.errors.clear('name')"
                    focus
                    required
                />
                <app-input
                    v-model="editForm.username"
                    placeholder="Username"
                    :error="editForm.errors.get('username')"
                    @updated="editForm.errors.clear('username')"
                    required                    
                    alpha-dash
                    lowercase                   
                />
                <app-input
                    type="email"
                    v-model="editForm.email"
                    placeholder="Email"
                    :error="editForm.errors.get('email')"
                    @updated="editForm.errors.clear('email')"
                    required                    
                />
                <app-select
                    v-model="editForm.roles"
                    placeholder="Roles"
                    :error="editForm.errors.get('roles')"
                    @updated="editForm.errors.clear('roles')"
                    :options="items.roles"
                    value-key="id"
                    label-key="name"
                    multiple
                />
                <app-select
                    v-model="editForm.permissions"
                    placeholder="Specific Permissions"
                    :error="editForm.errors.get('permissions')"
                    @updated="editForm.errors.clear('permissions')"
                    :options="availablePermissions"
                    value-key="id"
                    label-key="name"
                    multiple
                />
                <app-select
                    v-model="editForm.timezone"
                    placeholder="Timezone"
                    :error="editForm.errors.get('timezone')"
                    @updated="editForm.errors.clear('timezone')"
                    :options="items.timezones"
                    value-key="id"
                    value-label="label"
                    required
                />
                <div class="mt-5 flex items-center justify-end">
                    <app-button 
                        class="btn-white" 
                        @click="showEditModal = false"
                        :disable="editFormIsLoading"
                    >Cancel</app-button>
                    <app-button 
                        class="btn-blue ml-3" 
                        @click="update"
                        :loading="editFormIsLoading"
                    >Save</app-button>
                </div>
            </template>
        </app-modal>
        <app-modal
            :show.sync="showAddModal"
            width="md:w-1/2 lg:w-1/4"
            @close="addModalClosed"
        >
            <template slot="header">Add User</template>
            <template slot="body">
                <app-input
                    v-model="addForm.name"
                    placeholder="Name"
                    :error="addForm.errors.get('name')"
                    @updated="addForm.errors.clear('name')"
                    focus
                    required
                />
                <app-input
                    v-model="addForm.username"
                    placeholder="Username"
                    :error="addForm.errors.get('username')"
                    @updated="addForm.errors.clear('username')"
                    required                    
                    alpha-dash
                    lowercase                   
                />
                <app-input
                    type="email"
                    v-model="addForm.email"
                    placeholder="Email"
                    :error="addForm.errors.get('email')"
                    @updated="addForm.errors.clear('email')"
                    required                    
                />
                <app-select
                    v-model="addForm.roles"
                    placeholder="Roles"
                    :error="addForm.errors.get('roles')"
                    @updated="addForm.errors.clear('roles')"
                    :options="items.roles"
                    value-key="id"
                    label-key="name"
                    multiple
                />
                <app-select
                    v-model="addForm.permissions"
                    placeholder="Specific Permissions"
                    :error="addForm.errors.get('permissions')"
                    @updated="addForm.errors.clear('permissions')"
                    :options="items.permissions"
                    value-key="id"
                    label-key="name"
                    multiple
                />
                <app-select
                    v-model="addForm.timezone"
                    placeholder="Timezone"
                    :error="addForm.errors.get('timezone')"
                    @updated="addForm.errors.clear('timezone')"
                    :options="items.timezones"
                    value-key="id"
                    value-label="label"
                    required
                />
                <div class="mt-5 flex items-center justify-end">
                    <app-button 
                        class="btn-white" 
                        @click="showAddModal = false"
                        :disable="addFormIsLoading"
                    >Cancel</app-button>
                    <app-button 
                        class="btn-blue ml-3" 
                        @click="store"
                        :loading="addFormIsLoading"
                    >Add</app-button>
                </div>
            </template>
        </app-modal>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex';
    import Form from '@/helpers/Form';
    import Http from '@/helpers/Http';
    
    export default {
        data() {
            return {
                addForm: new Form({
                    name: null,
                    username: null,
                    email: null,
                    roles: [],
                    permissions: [],
                    timezone: null,
                }),
                editForm: new Form({
                    id: null,
                    name: null,
                    username: null,
                    email: null,
                    roles: [],
                    permissions: [],
                    timezone: null,
                }),
                availablePermissions: [],
                editFormIsLoading: false,
                addFormIsLoading: false,
                showEditModal: false,
                showAddModal: false,
                refreshTable: false,
                actions: [
                    {
                        label: 'Edit',
                        icon: () => import('@/icons/EditOutlined.vue'),
                        handler: users => {
                            this.showEditModal = true;
                            this.availablePermissions = users[0].available_permissions;
                            
                            this.editForm.set({
                                id: users[0].id,
                                name: users[0].name,
                                username: users[0].username,
                                email: users[0].email,
                                permissions: users[0].direct_permissions,
                                roles: users[0].roles,
                                timezone: users[0].timezone,
                            });
                        }
                    },
                    {
                        label: 'Activate',
                        icon: () => import('@/icons/VisibilityOutlined.vue'),
                        requiredSelect: true,
                        handler: users => {
                            this.activate(users[0]);
                        },
                        isEligible(users) {
                            return users[0].soft_deleted;
                        }
                    },
                    {
                        label: 'Deactivate',
                        icon: () => import('@/icons/VisibilityOffOutlined.vue'),
                        requiredSelect: true,
                        handler: users => {
                            this.deactivate(users[0]);
                        },
                        isEligible(users) {
                            return !users[0].soft_deleted && !users[0].super_admin;
                        }
                    },
                    {
                        label: 'Delete',
                        icon: () => import('@/icons/DeleteOutlined.vue'),
                        requiredSelect: true,
                        handler: users => {
                            this.deleteUser(users[0]);
                        },
                        isEligible(users) {
                            return !users[0].super_admin;
                        }
                    },
                    {
                        label: 'Add',
                        icon: () => import('@/icons/Add.vue'),
                        handler: () => {
                            this.showAddModal = true;
                        },
                    },
                    {
                        label: 'Enable 2FA',
                        icon: () => import('@/icons/PhonelinkLockOutlined.vue'),
                        isEligible(users) {
                            return !users[0]['2fa_enabled'];
                        },
                        handler: users => {
                            this.$swal.fire({
                                type: 'warning',
                                title: 'Are you sure?',
                                html: `You are about to enable two factor authentication for <strong>${users[0].name}</strong>. An instruction email will be sent to <b>${users[0].email}</b>.`,
                                showCancelButton: true,
                                confirmButtonColor: '#3182ce',
                                confirmButtonText: 'Yes, enable it!',
                                showLoaderOnConfirm: true,
                                preConfirm: (response) => {
                                    return Http.patch(`admin/users/${users[0].id}/2fa/enable`);
                                },
                                allowOutsideClick: () => !this.$swal.isLoading()
                            })
                            .then((result) => {
                                if (result.value) {
                                    this.refreshTable = true;
                                    this.$swal.fire({
                                        type: 'success',
                                        title: 'Success!',
                                        text: 'Two factor authentication was successfully enabled.'
                                    })
                                }
                            })
                        }
                    },                    
                    {
                        label: 'Re-Authenticate 2FA',
                        icon: () => import('@/icons/ScreenLockRotationOutlined.vue'),
                        isEligible(users) {
                            return users[0]['2fa_enabled'];
                        },
                        handler: users => {
                            this.$swal.fire({
                                type: 'warning',
                                title: 'Are you sure?',
                                html: `You are about to re-authenticate two factor authentication for <strong>${users[0].name}</strong>. An instruction email will be sent to <b>${users[0].email}</b>.`,
                                showCancelButton: true,
                                confirmButtonColor: '#f56565',
                                confirmButtonText: 'Yes, re-authenticate it!',
                                showLoaderOnConfirm: true,
                                preConfirm: (response) => {
                                    return Http.patch(`admin/users/${users[0].id}/2fa/reauth`);
                                },
                                allowOutsideClick: () => !this.$swal.isLoading()
                            })
                            .then((result) => {
                                if (result.value) {
                                    this.refreshTable = true;
                                    this.$swal.fire({
                                        type: 'success',
                                        title: 'Success!',
                                        text: 'Two factor authentication was successfully re-authenticated.'
                                    })
                                }
                            })
                        }
                    },
                    {
                        label: 'Disable 2FA',
                        icon: () => import('@/icons/PhonelinkOffOutlined.vue'),
                        isEligible(users) {
                            return users[0]['2fa_enabled'];
                        },
                        handler: users => {
                            this.$swal.fire({
                                type: 'warning',
                                title: 'Are you sure?',
                                html: `You are about to disable two factor authentication for <strong>${users[0].name}</strong></b>.`,
                                showCancelButton: true,
                                confirmButtonColor: '#f56565',
                                confirmButtonText: 'Yes, disable it!',
                                showLoaderOnConfirm: true,
                                preConfirm: (response) => {
                                    return Http.patch(`admin/users/${users[0].id}/2fa/disable`);
                                },
                                allowOutsideClick: () => !this.$swal.isLoading()
                            })
                            .then((result) => {
                                if (result.value) {
                                    this.refreshTable = true;
                                    this.$swal.fire({
                                        type: 'success',
                                        title: 'Success!',
                                        text: 'Two factor authentication was successfully disabled.'
                                    })
                                }
                            })
                        }
                    },
                ]
            }
        },
        computed: {
            ...mapGetters({
                items: 'admin/users/items',
            }),
        },
        methods: {
            editModalClosed() {
                this.editForm.reset();
                this.editForm.errors.clear();                
            },
            update(params) {
                this.editFormIsLoading = true;
                this.$store.dispatch('admin/users/update', this.editForm.payload())
                    .then(() => {
                        this.editFormIsLoading = false;
                        this.showEditModal = false;
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'User was updated successfully.'
                        })
                    })
                    .catch(({ response }) => {
                        this.editFormIsLoading = false;
                        this.editForm.errors.record(response.data);
                    });
            },
            addModalClosed() {
                this.addForm.reset();
                this.addForm.errors.clear();
            },
            add() {
                this.showAddModal = true;
            },
            store() {
                this.addFormIsLoading = true;
                this.$store.dispatch('admin/users/store', this.addForm.payload())
                    .then(() => {
                        this.addFormIsLoading = false;
                        this.showAddModal = false;
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'User was added successfully.'
                        })
                    })
                    .catch(({ response }) => {
                        this.addFormIsLoading = false;
                        this.addForm.errors.record(response.data);
                    })
            },
            deactivate(user) {
                this.$swal.fire({
                    type: 'warning',
                    title: 'Are you sure?',
                    html: `You are about to deactivate "<strong>${user.name}</strong>" account.`,
                    showCancelButton: true,
                    confirmButtonColor: '#f56565',
                    confirmButtonText: 'Yes, deactivate it!',
                    showLoaderOnConfirm: true,
                    preConfirm: (response) => {
                        return this.$store.dispatch('admin/users/deactivate', user.id);
                    },
                    allowOutsideClick: () => !this.$swal.isLoading()
                })
                .then((result) => {
                    if (result.value) {
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'User was successfully deactivated.'
                        })
                    }
                })                
            },
            activate(user) {
                this.$swal.fire({
                    type: 'warning',
                    title: 'Are you sure?',
                    html: `You are about to activate "<strong>${user.name}</strong>" account.`,
                    showCancelButton: true,
                    confirmButtonColor: '#f56565',
                    confirmButtonText: 'Yes, activate it!',
                    showLoaderOnConfirm: true,
                    preConfirm: (response) => {
                        return this.$store.dispatch('admin/users/activate', user.id);
                    },
                    allowOutsideClick: () => !this.$swal.isLoading()
                })
                .then((result) => {
                    if (result.value) {
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'User was successfully activated.'
                        })
                    }
                })                
            },
            deleteUser(user) {
                this.$swal.fire({
                    type: 'warning',
                    title: 'Are you sure?',
                    html: `You are about to delete "<strong>${user.name}</strong>" account permanently.`,
                    showCancelButton: true,
                    confirmButtonColor: '#f56565',
                    confirmButtonText: 'Yes, delete it!',
                    showLoaderOnConfirm: true,
                    preConfirm: (response) => {
                        return this.$store.dispatch('admin/users/delete', user.id);
                    },
                    allowOutsideClick: () => !this.$swal.isLoading()
                })
                .then((result) => {
                    if (result.value) {
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'User was successfully deleted.'
                        })
                    }
                })                
            },
        }
    }
</script>